import { useCallback, useEffect, useState } from "react";
import { CopyTaskParametersDc, ResourceTypeLink } from "@evergis/api";

import { useProgress } from "hooks/scheduler/useProgress";
import { collectErrorLog } from "utils/collectErrorLog";
import { copyTask } from "resources/scheduler";
import { ExportFormat } from "../types";
import { LayerTaskDataStorage, TableTaskDataStorage, TaskDataType } from "../../../../interfaces/scheduler";

interface ExportState {
  exportInProgress: boolean;
  processed: number;
  exportDone: boolean;
  taskId: string;
  runExport: VoidFunction;
  resetExport: VoidFunction;
}

interface ExportErrorState {
  message: string;
  count: number;
  log: string;
}

export const useExportPolling = (
  sourceName: string,
  targetName: string,
  exportTaskParams: CopyTaskParametersDc,
): [ExportState, ExportErrorState] => {
  const [runCopyTask, { data: taskResult }] = copyTask.use();
  const [copyInProgress, setCopyInProgress] = useState(false);
  const { processed, isTaskCompleted, result, reset: resetExport } = useProgress(taskResult?.taskId);
  const [error, setError] = useState<string>();
  const [errorsCount, setErrorsCount] = useState(0);
  const [errorLog, setErrorLog] = useState<string>();

  const runExport = useCallback(() => {
    if (!targetName) return;

    setCopyInProgress(true);
    runCopyTask({ input: exportTaskParams });
  }, [targetName, exportTaskParams]);

  useEffect(() => {
    if (isTaskCompleted) {
      setCopyInProgress(false);
    }
  }, [isTaskCompleted]);

  useEffect(() => {
    if (result && result.stepResults) {
      const { errorCount, message } = result;
      const log = collectErrorLog(result.stepResults);

      message && setError(message);
      errorCount && setErrorsCount(errorCount);
      log && setErrorLog(log);
    }
  }, [result]);

  return [
    {
      exportInProgress: copyInProgress,
      processed,
      exportDone: isTaskCompleted,
      taskId: taskResult && taskResult.taskId,
      runExport,
      resetExport,
    },
    { message: error, count: errorsCount, log: errorLog },
  ];
};

const getExportTaskDataStorage = (
  sourceType: ResourceTypeLink,
  resourceName: string,
): LayerTaskDataStorage | TableTaskDataStorage => {
  switch (sourceType) {
    case ResourceTypeLink.Table: {
      return {
        type: TaskDataType.Table,
        tableName: resourceName,
      };
    }
    default: {
      return {
        type: TaskDataType.Layer,
        serviceName: resourceName,
      };
    }
  }
};
const getTargetType = (format: ExportFormat): TaskDataType => {
  switch (format) {
    case "Csv": {
      return TaskDataType.Csv;
    }
    case "XLSX": {
      return TaskDataType.Excel;
    }
    default: {
      return TaskDataType.Static;
    }
  }
};

export function getExportTaskParams(options: {
  sourceName: string;
  sourceType: ResourceTypeLink;
  targetName: string;
  format: ExportFormat;
  columnDelimiter: string;
  isWkt: boolean;
  condition?: string;
}): CopyTaskParametersDc {
  const { sourceName, sourceType, targetName, format, columnDelimiter, isWkt, condition } = options;

  return {
    source: getExportTaskDataStorage(sourceType, sourceName),
    target: {
      type: getTargetType(format),
      layerName: sourceName,
      fileName: targetName,
      columnDelimiter: format === "Csv" ? columnDelimiter : undefined,
      isWkt,
    },
    condition,
  };
}
